<template>
  <v-card flat color="transparent" class="bookmark-item--list">
    <div class="d-flex align-start">
      <BulkSelectCheckbox
        v-if="isBulkActionsActive"
        class="mx-3 mt-0"
        :checked="isSelected"
        @click="toggleSelect"
      />
      <v-img
        :src="bookmark.isFile ? bookmark.thumbnailUrl : bookmark.faviconUrl" :aspect-ratio="1" :width="display.mdAndUp.value ? 32 : 24"
        class="flex-grow-0 rounded" contain
        :class="bookmark.hasFavicon ? 'neutral25' : bookmark.colorClasses"
      />

      <div class="card-content flex-grow-1 of-hidden px-4">
        <div class="text-neutral400 d-flex align-center mb-2 mt-md-1 text-caption text-md-body-2">
          <BookmarkSource :bookmark="bookmark" no-icon />
          <span class="mx-1">&middot;</span>
          <BookmarkDate :value="bookmark.createdAt" />
        </div>

        <div
          class="bookmark-link text-neutral900 d-block cursor-pointer"
          :title="bookmark.title"
          @click="onContentClick"
        >
          <h4
            class="bookmark-headline text-body-1 mb-1 font-weight-semibold d-flex align-center"
          >
            <v-progress-circular
              v-if="bookmark.isLoading && !bookmark.hasSufficientData"
              indeterminate size="16" color="blue400" width="3"
              class="mr-2"
            />
            <span class="title-text" v-text="bookmark.title ?? bookmark.domain" />
            <BrandButton
              v-if="!isBulkActionsActive && hasSource"
              class="outgoing-link-btn ml-2"
              size="x-small"
              icon
              flat
              :title="t(bookmark.isImage ? 'cta.download_original' : 'cta.view_original_content')"
              @click.stop.prevent="openSource"
            >
              <PhArrowSquareOut class="text-body-1 text-neutral600" />
            </BrandButton>
          </h4>

          <p
            v-if="bookmark.description"
            class="bookmark-description text-body-2 text-neutral600 mb-3"
            v-text="bookmark.description"
          />
        </div>

        <BookmarkTags v-if="bookmark.tags.length" class="mb-3" :tags="bookmark.tags" @tag-selected="onTagSelected" />

        <BookmarkActions
          :bookmark="bookmark" :user-id="userId"
          :public-mode="publicMode"
          :membership="membership"
          class="text-text"
          @favorite="$emit('favorite')"
          @save="$emit('save')"
          @add-to-collection="$emit('add-to-collection')"
          @add-to-collection-from-collection="$emit('add-to-collection-from-collection')"
          @comments="$emit('comments')"
          @like="$emit('like')"
          @likes="$emit('likes')"
          @pin-edit="$emit('pin-edit')"
        />
      </div>
    </div>
  </v-card>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { useRoute, useRouter } from 'vue-router'
import { PhArrowSquareOut } from '@phosphor-icons/vue'
import BookmarkSource from './BookmarkSource'
import BookmarkActions from './BookmarkActions'
import BookmarkDate from './BookmarkDate'
import BookmarkTags from './BookmarkTags'
import BrandButton from '@/components/global/brand/BrandButton.vue'
import { useBulkActionsV2 } from '@/composables/useBulkActionsV2'
import { useBookmarkItemSourceUrl } from '@/composables/useBookmarkSourceUrl'
import BulkSelectCheckbox from '@/components/BulkSelectCheckbox.vue'


const props = defineProps({
  bookmark: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'toggle-select',
  'tag-selected',
  'favorite',
  'save',
  'add-to-collection',
  'add-to-collection-from-collection',
  'comments',
  'like',
  'likes',
  'pin-edit',
])

const { t } = useI18n()
const display = useDisplay()
const route = useRoute()
const router = useRouter()
const { isBulkActionsActive, isItemSelected } = useBulkActionsV2()

const isSelected = computed(() => isItemSelected(props.bookmark.id))

const detailRoute = computed(() => {
  if (props.publicMode) {
    const username = route.params.username
    const cid = route.params.id || props.bookmark.ownerId
    return `/u/${username}/collections/${cid}/bookmarks/${props.bookmark.id}`
  }
  if (props.bookmark.isCollectionBookmark) {
    return `/collections/${props.bookmark.ownerId}/bookmarks/${props.bookmark.id}`
  }
  return `/bookmarks/${props.bookmark.id}`
})

function onContentClick() {
  if (isBulkActionsActive.value) {
    toggleSelect()
  } else {
    router.push(detailRoute.value)
  }
}

const { openSource, hasSource } = useBookmarkItemSourceUrl(props)

function toggleSelect() {
  emit('toggle-select', props.bookmark)
}

function onTagSelected(tag) {
  emit('tag-selected', tag)
}
</script>

<style lang="scss" scoped>
.bookmark-link {
  text-decoration: none;
  color: inherit;
}

.bookmark-headline {
  word-break: break-word;

  .title-text {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    overflow: hidden;
  }
}

.bookmark-description {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  display: -webkit-box;
  overflow: hidden;
}

.outgoing-link-btn {
  flex-shrink: 0;
}
</style>
